import type {
  AddressVerification,
  Booking,
  BookingStatus,
  OfficeTimestamp,
  SentMessage,
} from './types';

// Firestore Timestamps serialize as { _seconds, _nanoseconds }; the display value is
// _seconds * 1000 (BOOKINGS_ADMIN_SPEC types note). Tolerate already-ISO strings just in
// case the upstream shape ever changes.
export function ts_to_date(t: OfficeTimestamp | string | null | undefined): Date | null {
  if (!t) return null;
  if (typeof t === 'string') {
    const d = new Date(t);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof t._seconds === 'number') return new Date(t._seconds * 1000);
  return null;
}

export function fmt_dt(t: OfficeTimestamp | string | null | undefined): string {
  const d = ts_to_date(t);
  return d ? d.toLocaleString() : '—';
}

export const STATUS_OPTIONS: BookingStatus[] = [
  'BOOKED',
  'PENDING',
  'PENDING_NO_PHONE',
  'CONFIRMED',
  'RESCHEDULED',
  'EXPIRED',
  'CANCELLED',
];

export const KIND_OPTIONS = ['estimate', 'demo', 'callback'] as const;

export function status_color(s: BookingStatus | undefined): string {
  switch (s) {
    case 'BOOKED':
    case 'CONFIRMED':
      return 'var(--green-700)';
    case 'PENDING':
    case 'PENDING_NO_PHONE':
    case 'RESCHEDULED':
      return '#b58900';
    case 'EXPIRED':
    case 'CANCELLED':
      return '#b3261e';
    default:
      return 'var(--muted)';
  }
}

// Color for a sent_message / resend-result status (sent | failed | skipped_disabled).
export function message_status_color(status: string): string {
  if (status === 'sent') return 'var(--green-700)';
  if (status === 'failed') return '#b3261e';
  return 'var(--muted)';
}

// Address-validation verdict → display intent. corrected/ambiguous/not_found warrant
// operator attention (spec §4.2): corrected shows a raw→corrected diff, ambiguous/not_found
// a warning badge.
export function verification_meta(v: AddressVerification | undefined): {
  label: string;
  color: string;
  warn: boolean;
} {
  switch (v) {
    case 'confirmed':
      return { label: 'Confirmed', color: 'var(--green-700)', warn: false };
    case 'corrected':
      return { label: 'Corrected', color: '#b58900', warn: true };
    case 'ambiguous':
      return { label: 'Ambiguous', color: '#b3261e', warn: true };
    case 'not_found':
      return { label: 'Not found', color: '#b3261e', warn: true };
    case 'not_validated':
      return { label: 'Not validated', color: 'var(--muted)', warn: false };
    default:
      return { label: '—', color: 'var(--muted)', warn: false };
  }
}

// Row-level "where/company": the property address for on-site work, the prospect's company
// for a demo.
export function where_or_company(b: Booking): string {
  if (b.booking_kind === 'demo') return b.prospect_company ?? '—';
  return b.client_address ?? '—';
}

// Row-level contact preference.
export function contact_of(b: Booking): string {
  return b.client_phone ?? b.prospect_email ?? (b.client_email_only ? 'email only' : '—');
}

// Only surface a recording link if it's a real http(s) URL. The value comes from a
// trusted first-party service today, but rendering an upstream string straight into an
// href would be DOM XSS (e.g. a javascript: URI) if that ever changed — so validate the
// scheme before emitting the anchor. Returns null when unsafe/unparseable.
export function safe_http_url(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? url : null;
  } catch {
    return null;
  }
}

// The call recap is the body of the latest email sent_message (spec §5 — read from
// sent_messages, not the booking doc).
export function recap_from_messages(messages: SentMessage[]): string | null {
  const emails = messages
    .filter((m) => m.channel === 'email' && m.body)
    .sort((a, b) => (ts_to_date(b.created_at)?.getTime() ?? 0) - (ts_to_date(a.created_at)?.getTime() ?? 0));
  return emails[0]?.body ?? null;
}

// Distinct recipients across the messages we'd replay, for the confirm prompt.
// Restricts to the channels being re-sent when given.
export function recipients_for(
  messages: SentMessage[],
  channels?: ('email' | 'sms')[],
): string[] {
  const set = new Set<string>();
  for (const m of messages) {
    if (channels && !channels.includes(m.channel)) continue;
    for (const to of m.to ?? []) set.add(to);
  }
  return [...set];
}
